import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Inject,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { TestActionsService } from '@ispm/test/services/test-actions.service';
import { cloneDeep, get } from 'lodash';
import { DateHandler } from '@ispm/shared/utils/DateHandler';
import { flatMap, map, pluck, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { ResourceReference } from '@ispm/core/classes/resource-reference.class';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { Resources } from '@ispm/workflow/constants/resources.enum';
import { of } from 'rxjs/observable/of';
import { TestService } from '@ispm/dashboard/test/services/test.service';

@Component({
  selector: 'gen-sample-generic',
  templateUrl: './sample-generic.component.html',
  styleUrls: ['./sample-generic.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class SampleGenericComponent implements OnInit, OnDestroy {
  @Input() sample: any;
  @Input() overrides: any;
  @Input() callback: () => void;
  @Input() index: number;
  @Input() allowedit: boolean;
  @Input() markAsPrimaryEnabled: boolean;
  @Input() proceedToAnaliseStarted: boolean;
  @Input() editMode;
  @Input() disabledForced: boolean;
  @Output() sampleUpdate: EventEmitter<any> = new EventEmitter<any>();
  @Output() primarySampleChanged: EventEmitter<any> = new EventEmitter<any>();
  public changedFieldsReferences: ResourceReference[] = [];
  //public testService: any;
  private _diffFields: Set<string> = new Set<string>();
  private destroyer$: Subject<null> = new Subject<null>();
  get diffFields() {
    return Array.from(this._diffFields);
  }
  set diffFields(fields: Array<string>) {
    this._diffFields = new Set<string>(fields);
  }

  constructor(
    private detector: ChangeDetectorRef,
    private testActionsService: TestActionsService,
    private formHandler: FormHandlerService,
    private workflowConfigService: WorkflowConfigService,
    public testService : TestService
  ) {
  }

  ngOnInit() {
    this.testService.loadTestInfoCollections();
    this.sample = this.init(this.sample);
    this.changedFieldsReferences.push(new ResourceReference(this.sample.id, 'Specimen'));
    this.workflowConfigService.getResourcesMap().pipe(
      take(1),
      pluck(Resources.RequestedRevision)
    ).subscribe((revisionId: string) => {
      this.changedFieldsReferences.push(new ResourceReference(revisionId, 'WorkflowRevision'));
      this.getChangedFields();
    });
  }


  init(sample) {
    sample = cloneDeep(sample);

    if (!sample.collection) {
      sample.collection = { };
    }
    else if (sample.collection.collectedDateTime) {
      sample.collection.collectedDateTime = (<any> DateHandler.removeTimezone(new Date(sample.collection.collectedDateTime))).toPlatformFormat();
    }

    if (sample.receivedTime) {
      sample.receivedTime = (<any> DateHandler.removeTimezone(new Date(sample.receivedTime))).toPlatformFormat();
    }

    if (sample.IHCStatus) {
      sample.ihcDisplay = sample.IHCStatus.map(s => s.display).join(',');
    }

    if (sample.status && sample.status.contains('Sequencing Complete')) {
      sample.complete = true;
    }

    return sample;
  }

  onUpdate(updateEvent: { sample: any, field: string}) {
    const sample = updateEvent.sample;
    const updatedFields = [...this.diffFields, updateEvent.field];

    return this.testActionsService.UpdateSpecimen(sample).pipe(
      flatMap(res => {
        if (this.sample.objectType.toLowerCase() === 'dynamic') {
          return of(res);
        }

        return this.formHandler.setChangedFields(this.changedFieldsReferences, updatedFields)
          .pipe(
            map(fieldsRes => {
              this.diffFields = fieldsRes.fields;

              return res;
            })
          );
      })
    ).subscribe((res) => {
      Object.assign(this.sample, this.init(res));
      this.sampleUpdate.emit(res); // res and not this.sample since we don't want transformed date on diagnostic order(report issue)

      try {
        this.detector.detectChanges();
      } catch (err) {
        console.error('sample-generic component: ChangeDetectorRef was destroyed.');
      }
    });
  }

  onPrimarySet(setAsPrimaryEvent: { sampleId: string, sampleType: string, workflowRequest: string}) {
    return this.testActionsService.markSpecimenAsPrimary({
      sampleId: setAsPrimaryEvent.sampleId,
      sampleType: setAsPrimaryEvent.sampleType,
      workflowRequest: setAsPrimaryEvent.workflowRequest
    }).pipe(
      flatMap(res => {
        return of(res);
      }),
      takeUntil(this.destroyer$.asObservable())
    ).subscribe((res) => {
      this.primarySampleChanged.emit(res); // res and not this.sample since we don't want transformed date on diagnostic order(report issue)
      this.detector.markForCheck();
    });
  }

  getChangedFields() {
    if (!!get(this.sample, 'objectType') && get(this.sample, 'objectType').toLowerCase() === 'dynamic') {
      return;
    }

    this.formHandler.getChangedFields(`[${this.changedFieldsReferences.map(ref => ref.reference).join(',')}]`)
      .pipe(
        takeUntil(this.destroyer$.asObservable())
      )
      .subscribe(fields => {
        this.diffFields = fields;
      });
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }
}
